import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Typography, Grid, Box, Container, Card, CardHeader, Divider, CardContent, Hidden, IconButton, Toolbar, TextField, Checkbox, ListItemText, MenuItem, Dialog, DialogTitle, DialogContent, Alert, DialogActions, Button } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import { connect } from 'react-redux';
import {
  Truck as TruckIcon,
  Edit as EditIcon
} from 'react-feather';
import GlobalSearch from '../components/GlobalSearch';
import FilterBy from '../components/FilterBy';
import Table from '../components/Table';
import MobileDataList from '../components/MobileDataList';
import AlertIndicatorIcon from '../components/AlertIndicatorIcon';
import AppConstants from '../constants/AppConstants';
import { refreshData, reset, searchByText, searchByWarning, changePage, changeSortModel, changeFilterByColumn, selectStates, updateSelectionStatus } from '../store/actions/vehicleActions';
import FormattedVehicle from '../models/FormattedVehicle';
import { RootState } from '../store/store';
import EnumService from '../services/EnumService';
import { EnumRes } from '../proxy/proxy';
import MultiRowActionButton from '../components/MultiRowActionButton';
import { showAlert as showGlobalAlert, clearAllAlerts as clearAllGlobalAlerts } from '../store/actions/alertActions';
import VehiclesService from '../services/VehiclesService';
import { formatErrorMessage, formatSuccessMessage } from '../utils/common';
import { hide, show } from '../store/actions/busyIndicatorActions';

interface Props {
  visibleRows: Array<FormattedVehicle>,
  selectedRowIds: Array<number>,
  totalRowCount: number,
  searchedText: string,
  selectedWarning: string,
  currentPage: number,
  sortModel: any,
  selectedStates: Array<number>,
  filterByCol: any,
  fetchAllRows: any,
  resetPage: any,
  filterByText: any,
  filterByWarning: any,
  onPageChange: any,
  onSelectionModelChange: any,
  onSort: any,
  onChangeFilterByColumn: any,
  setStates: (states: Array<number>) => void,
  showAlert: (message: any) => any,
  clearAllAlerts: () => any,
  showBusyIndicator: () => any,
  hideBusyIndicator: () => any,
}

const Vehicles: React.FC<Props> = ({
  visibleRows,
  totalRowCount,
  selectedRowIds,
  searchedText,
  selectedWarning,
  currentPage,
  sortModel,
  filterByCol,
  selectedStates,
  onSelectionModelChange,

  fetchAllRows,
  resetPage,
  filterByText,
  filterByWarning,
  onPageChange,
  onSort,
  onChangeFilterByColumn,
  setStates,
  showAlert,
  clearAllAlerts,
  showBusyIndicator,
  hideBusyIndicator
}) => {
  const navigate = useNavigate();
  const [allStates, setAllStates] = useState<Array<EnumRes>>([]);
  const [openDeleteWarning, setOpenDeleteWarning] = React.useState(false);

  const onRowClick = ({ id }: any) => {
    navigate(`/app/vehicle/${id}`);
  };

  const columns: Array<any> = [
    {
      field: 'registrationNumber',
      headerName: 'Registration Number',
      width: 250,
    },
    {
      field: 'vehicleType',
      headerName: 'Type',
      width: 200,
    },
    {
      field: 'location',
      headerName: 'Location',
      width: 200,
    },
    {
      field: 'vehicleStatus',
      headerName: 'Status',
    },
    { field: 'state', headerName: 'State', flex: 1 },
    {
      field: 'maxCapacityInKgs',
      headerName: 'Max Payload',
      width: 200,
      renderCell: (params: any) => {
        const { value } = params;
        return (
          <span>
            {value != null ? `${value} Kgs` : ''}
          </span>
        );
      }
    },
    {
      field: 'odometerKms',
      headerName: 'Odometer Reading',
      width: 200,
      renderCell: (params: any) => {
        const { value } = params;
        return (
          <span>
            {value || 0} kms
          </span>
        );
      }
    },
    // {
    //   field: 'nextServiceDueKms',
    //   headerName: 'Next Service Due Kms',
    //   flex: 1,
    //   renderCell: (params: any) => {
    //     const { value } = params;
    //     return (
    //       <span>
    //         {value || 0} kms
    //       </span>
    //     );
    //   }
    // },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'number',
      sortable: false,
      filterable: false,
      renderCell: (params: any) => {
        const { row } = params;
        return (
          <IconButton size="medium" aria-label="edit" onClick={() => onRowClick(row)}>
            <EditIcon />
          </IconButton>
        );
      },
      renderHeader: () => (<span />)
    },
  ];

  const onSearchTextChange = (event: any) => {
    filterByText(event?.target?.value);
  };

  const onWarningSelectChange = (event: any) => {
    filterByWarning(event.target.value);
  };

  const refresh = () => {
    resetPage();
    fetchAllRows();
  };

  useEffect(() => {
    fetchAllRows();
    EnumService.getEnums('states').then((states) => {
      setAllStates(states);
    }).catch();
  }, []);

  const addVehicle = () => {
    navigate('/app/vehicle/add');
  };

  const onStatesSelect = (event: any) => {
    setStates(event?.target?.value ?? []);
  };

  const getRowClassName = (row: FormattedVehicle) => {
    if (row.alerts?.length) {
      return row.alerts.find((alert: any) => alert.type === 'error') ? 'error' : 'warning';
    }
    return '';
  };

  const filterOptions = [
    { value: 'all', text: 'Show All' },
    { value: 'service', text: 'Service' },
    { value: 'insurance', text: 'Insurance' },
    { value: 'registration', text: 'Registration' }
  ];

  const stateDropdown = (
    <TextField
      sx={{ width: '200px' }}
      size="small"
      label="States"
      name="states"
      onChange={onStatesSelect}
      onBlur={onStatesSelect}
      select
      SelectProps={{
        multiple: true,
        renderValue: (selected: any) => selected.map((x: any) => allStates.find((state: any) => state.id === x)?.value).join(', ')
      }}
      value={selectedStates}
    >
      {allStates.map((option) => (
        <MenuItem key={option.id} value={option.id}>
          <Checkbox checked={selectedStates.includes(option.id ?? 0)} />
          <ListItemText primary={option.value} />
        </MenuItem>
      ))}
    </TextField>
  );

  const bulkDeleteVehicles = async () => {
    setOpenDeleteWarning(false);
    showBusyIndicator();
    clearAllAlerts();

    const deletePromises = selectedRowIds.map((id) =>
      VehiclesService.deleteVehicle(id)
        .then(() => ({ success: true }))
        .catch((err) => ({ success: false, error: formatErrorMessage(JSON.parse(err.response)) })));

    const results = await Promise.allSettled(deletePromises);
    hideBusyIndicator();

    let successCount = 0;
    let failureCount = 0;
    const failureMessages: string[] = [];

    results.forEach((res) => {
      if (res.status === 'fulfilled') {
        if (res.value.success) {
          successCount++;
        } else if ('error' in res.value) {
          failureCount++;
          failureMessages.push(res.value.error.message);
        }
      }
    });

    // Show success and failure messages
    if (successCount > 0) {
      showAlert(formatSuccessMessage(`${successCount} vehicle(s) deleted successfully.`));
      fetchAllRows();
    }
    if (failureCount > 0) {
      showAlert(formatErrorMessage(`${failureCount} vehicle(s) failed to delete:\n${failureMessages.join('\n')}`));
      fetchAllRows();
    }
  };

  const renderEachMobileCard = (row: FormattedVehicle) => (
    <Card key={row.id} sx={{ marginBottom: '10px' }} variant="outlined">
      <CardHeader
        avatar={
          <TruckIcon />
        }
        action={
          (
            <>
              <AlertIndicatorIcon alerts={row.alerts} />
              <IconButton aria-label="edit">
                <EditIcon />
              </IconButton>
            </>
          )
        }
        title={row.registrationNumber}
        subheader={`${row.vehicleType} - ${row.vehicleStatus}`}
        onClick={() => onRowClick(row)}
      />
      <Divider />
      <CardContent>
        <Grid container>
          {
            row.vin && (
              <Grid item xs={12}>
                <Typography variant="body2">
                  <b>VIN:</b> {row.vin}
                </Typography>
              </Grid>
            )
          }
          {
            row.state && (
              <Grid item xs={12}>
                <Typography variant="body2">
                  <b>State:</b> {row.state}
                </Typography>
              </Grid>
            )
          }
          <Grid item xs={12}>
            <Typography variant="body2">
              <b>Odometer Reading:</b> {row.odometerKms || 0} kms
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2">
              <b>Odometer Reading:</b> {row.odometerKms || 0} kms
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2">
              <b>Max Payload In Kgs:</b> {row.maxCapacityInKgs || ''}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <>
      <Helmet>
        <title>Vehicles | {AppConstants.APP_NAME}</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          py: 3
        }}
      >
        <Container maxWidth="xl">
          <Card>
            <CardHeader
              action={
                (
                  <>
                    <IconButton onClick={refresh}>
                      <RefreshIcon />
                    </IconButton>
                    <IconButton onClick={addVehicle}>
                      <AddIcon />
                    </IconButton>
                  </>
                )
              }
              title="Vehicles"
            />
            <Divider />
            <CardContent>
              <Hidden mdDown>
                <Toolbar style={{ justifyContent: 'flex-end' }}>
                  <MultiRowActionButton
                    selectedRowsCount={selectedRowIds?.length}
                    options={
                      [
                        {
                          label: 'Delete selected vehicles',
                          onClick: () => setOpenDeleteWarning(true)
                        }
                      ]
                    }
                    disabled={!selectedRowIds?.length}
                  />
                  <div style={{ flex: '1' }} />
                  {stateDropdown}
                  <FilterBy
                    label="Filter by Alerts"
                    options={filterOptions}
                    value={selectedWarning}
                    onChange={onWarningSelectChange}
                    width="280px"
                  />
                  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                  <GlobalSearch searchedText={searchedText} onSearchTextChange={onSearchTextChange} showFilterByColumn columns={columns} filterByColumn={filterByCol} onFilterByColumnChange={onChangeFilterByColumn} width="400px" />
                </Toolbar>
                <Table
                  rows={visibleRows}
                  columns={columns}
                  onRowDoubleClick={onRowClick}
                  totalRows={totalRowCount}
                  page={currentPage}
                  onPageChange={onPageChange}
                  sortModel={sortModel}
                  checkboxSelection
                  onSortModelChange={onSort}
                  selectionModel={visibleRows?.filter((row: any) => row.selected).map((row) => row.id) ?? []}
                  onSelectionModelChange={(selectedIds) => onSelectionModelChange(visibleRows, selectedIds)}
                  getRowClassName={(params: any) => getRowClassName(params?.row)}
                />
              </Hidden>
              <Hidden mdUp>
                <Toolbar sx={{ flexDirection: 'column' }}>
                  <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                    {stateDropdown}
                  </div>
                  <div style={{ width: '100%', padding: '5px 0' }}>
                    <FilterBy label="Filter by Alerts" options={filterOptions} value={selectedWarning} onChange={onWarningSelectChange} />
                  </div>
                  <div style={{ width: '100%', padding: '5px 0' }}>
                    <GlobalSearch searchedText={searchedText} onSearchTextChange={onSearchTextChange} showFilterByColumn columns={columns} filterByColumn={filterByCol} onFilterByColumnChange={onChangeFilterByColumn} />
                  </div>
                </Toolbar>
                <MobileDataList
                  rows={visibleRows}
                  renderEachRow={renderEachMobileCard}
                  totalRows={totalRowCount}
                  page={currentPage}
                  onPageChange={onPageChange}
                />
              </Hidden>
            </CardContent>
          </Card>
        </Container>
      </Box>
      <Dialog open={openDeleteWarning} onClose={() => setOpenDeleteWarning(false)}>
        <DialogTitle id="alert-dialog-title">Are you sure?</DialogTitle>
        <DialogContent>
          <Typography variant="h5" id="alert-dialog-description">Selected vehicles will be deleted. You may not be able to recover it later.</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setOpenDeleteWarning(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={bulkDeleteVehicles}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  visibleRows: state.vehicle?.filteredRows,
  selectedRowIds: state.vehicle?.allRows?.filter((row: any) => row.selected).map((row) => row.id ?? 0) ?? [],
  totalRowCount: state.vehicle?.allRows?.length,
  currentPage: state.vehicle?.currentPage,
  searchedText: state.vehicle?.searchedText,
  selectedWarning: state.vehicle?.filterBy,
  sortModel: state.vehicle?.sortModel,
  filterByCol: state.vehicle?.filterByCol,
  selectedStates: state.vehicle.states ?? [],
});

const mapDispatchToProps = (dispatch: any) => ({
  fetchAllRows: () => dispatch(refreshData()),
  resetPage: () => dispatch(reset()),
  filterByText: (text: string) => dispatch(searchByText(text)),
  filterByWarning: (text: string) => dispatch(searchByWarning(text)),
  onPageChange: (page: number) => dispatch(changePage(page)),
  onSort: (sortModel: any) => dispatch(changeSortModel(sortModel)),
  setStates: (states: Array<number>) => dispatch(selectStates(states)),
  onChangeFilterByColumn: (col: string) => dispatch(changeFilterByColumn(col)),
  onSelectionModelChange: (visibleRows: Array<any>, selectedRowIds: Array<number>) => dispatch(updateSelectionStatus(visibleRows, selectedRowIds)),
  showBusyIndicator: () => dispatch(show()),
  hideBusyIndicator: () => dispatch(hide()),
  showAlert: (message: any) => dispatch(showGlobalAlert(message)),
  clearAllAlerts: () => dispatch(clearAllGlobalAlerts())
});

export default connect(mapStateToProps, mapDispatchToProps)(Vehicles);
